/**
 * P云广告服务
 * 封装广告拉取和拦截逻辑
 * 支持多个广告位（按 spaceId 隔离状态）
 */

import { loadPyunSdk, isPyunSdkLoaded, getPyunInstance } from './pyunSdk'
import {
  pyunAppId,
  pyunSpaceId,
  pyunProviderCode,
  pyunProviderUrl,
  pyunExternal
} from '../../config/pyunConfig'
import { logger } from './pyunLogger'
import * as constant from './constant'

/** P云券包外跳后返回支付页时，用于恢复 isPress / code 的 session 标记 */
export const PYUN_COUPON_REDIRECT_KEYS = {
  PAY_MONEY: 'pyun_coupon_redirect_pay_money',
  ESCAPE_OUT: 'pyun_coupon_redirect_escape_out'
}

const PYUN_COUPON_REDIRECT_EXPIRE_MS = 30 * 60 * 1000

/**
 * 跳转 P云券包页前打标记，便于微信返回（bfcache）时恢复支付按钮
 */
export function markPyunCouponRedirect (storageKey, plateNumber) {
  sessionStorage.setItem(storageKey, JSON.stringify({
    plate_number: plateNumber || '',
    timestamp: Date.now()
  }))
}

function needRefreshPayCode (vm) {
  if (parseFloat(vm.actually_paid) > 0) return true
  const escData = vm.escapeInfo || vm.escData
  if (parseFloat(vm.actually_paid) === 0 && escData && Number(escData.total) > 0) return true
  if (parseFloat(vm.actually_paid) === 0 && vm.discount_coupon_ids_send && vm.discount_coupon_ids_send.length > 0) {
    return true
  }
  return false
}

/**
 * 从 P云券包外跳返回后解锁支付按钮并重新拉取微信 code
 */
export function recoverFromPyunCouponRedirect (storageKey, vm) {
  const raw = sessionStorage.getItem(storageKey)
  if (!raw) return false
  try {
    const data = JSON.parse(raw)
    if (!data.timestamp || Date.now() - data.timestamp > PYUN_COUPON_REDIRECT_EXPIRE_MS) {
      sessionStorage.removeItem(storageKey)
      return false
    }
    sessionStorage.removeItem(storageKey)
    vm.isPress = false
    vm.code = ''
    if (vm.$store && typeof vm.$store.commit === 'function') {
      vm.$store.commit(constant.keyCodeVuex, '')
    }
    if (needRefreshPayCode(vm) && typeof vm.getAllCode === 'function') {
      vm.getAllCode()
    }
    logger.info('[PyunAd] 从P云券包页返回，已恢复支付状态', { storageKey })
    return true
  } catch (err) {
    sessionStorage.removeItem(storageKey)
    logger.error('[PyunAd] 恢复支付状态失败', err)
    return false
  }
}

/**
 * 监听 pageshow / visibilitychange，处理外跳 P云后返回
 */
export function bindPyunCouponRedirectListeners (storageKey, vm) {
  const handler = () => recoverFromPyunCouponRedirect(storageKey, vm)
  const onPageShow = (e) => {
    if (e.persisted || sessionStorage.getItem(storageKey)) {
      handler()
    }
  }
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && sessionStorage.getItem(storageKey)) {
      handler()
    }
  }
  window.addEventListener('pageshow', onPageShow)
  document.addEventListener('visibilitychange', onVisibilityChange)
  return { onPageShow, onVisibilityChange }
}

export function unbindPyunCouponRedirectListeners (handlers) {
  if (!handlers) return
  window.removeEventListener('pageshow', handlers.onPageShow)
  document.removeEventListener('visibilitychange', handlers.onVisibilityChange)
}

// 广告状态（按 spaceId 隔离）
const adStateMap = {}

function getAdState (spaceId) {
  if (!adStateMap[spaceId]) {
    adStateMap[spaceId] = { loaded: false, data: null, extra: null, uri: null }
  }
  return adStateMap[spaceId]
}

/** 等待 DOM 节点就绪（券包容器等 v-if 异步挂载场景） */
export async function waitForAdElement (elId, maxRetries = 20, intervalMs = 100) {
  for (let i = 0; i < maxRetries; i++) {
    if (document.getElementById(elId)) {
      return true
    }
    await new Promise(resolve => setTimeout(resolve, intervalMs))
  }
  return false
}

function formatPyunError (cause) {
  if (!cause) return '未知错误'
  if (typeof cause === 'string') return cause
  if (cause.message) return cause.message
  if (cause.msg) return cause.msg
  try {
    return JSON.stringify(cause)
  } catch (e) {
    return String(cause)
  }
}

/** Plus 下券包品类：PASSER=券包/优惠支付，BENEFER=权益/安心支付 */
export const PYUN_COUPON_CATEGORY = {
  PASSER: 'PASSER',
  BENEFER: 'BENEFER'
}

/** Plus 产品 scheme 标识 */
export const PYUN_PLUS_SCHEME = 'plus'

/**
 * 测试开关（测完均改回 false；测 category 时 NON_PLUS 应为 false）
 * - PYUN_TEST_FORCE_NON_PLUS：强制 scheme≠plus
 * - PYUN_TEST_FORCE_INVALID_CATEGORY：强制 category 非 PASSER/BENEFER
 */
export const PYUN_TEST_FORCE_NON_PLUS = false
export const PYUN_TEST_FORCE_INVALID_CATEGORY = false

/**
 * 解析 ready 回调中的产品 scheme（如 plus）
 * @param {Object} e ready 回调事件
 * @returns {string}
 */
export function getCouponPayScheme (e) {
  if (PYUN_TEST_FORCE_NON_PLUS) return 'test-non-plus'
  const uri = e && e.uri
  if (!uri || uri.scheme == null) return ''
  return String(uri.scheme).trim()
}

/**
 * 从 ready 回调解析 Plus 子品类 category（PASSER | BENEFER）
 * @param {Object} e ready 回调事件
 * @returns {string}
 */
export function getCouponProductCategory (e) {
  if (PYUN_TEST_FORCE_INVALID_CATEGORY) return 'TEST_OTHER'
  const extra = e && e.extra
  if (!extra || extra.category == null) return ''
  return String(extra.category).trim().toUpperCase()
}

/**
 * category 是否为已支持的券包/权益品类
 * @param {string} category
 * @returns {boolean}
 */
export function isValidCouponCategory (category) {
  const c = category ? String(category).trim().toUpperCase() : ''
  return c === PYUN_COUPON_CATEGORY.PASSER || c === PYUN_COUPON_CATEGORY.BENEFER
}

/**
 * 是否为 Plus 产品（scheme=plus 或 link 以 plus:// 开头）
 * @param {Object} e ready 回调事件
 * @returns {boolean}
 */
export function isPlusProduct (e) {
  if (PYUN_TEST_FORCE_NON_PLUS) return false
  const scheme = getCouponPayScheme(e).toLowerCase()
  if (scheme === PYUN_PLUS_SCHEME) return true
  const uri = e && e.uri
  const link = uri && uri.link != null ? String(uri.link) : (e && e.link != null ? String(e.link) : '')
  return link.toLowerCase().startsWith(PYUN_PLUS_SCHEME + '://')
}

/**
 * 是否展示券包入口（双按钮 + 券包 banner + 拦截）
 * 须同时满足：Plus 且 category 为 PASSER 或 BENEFER
 * @param {Object} e ready 回调事件
 * @returns {boolean}
 */
export function isCouponEntryEnabled (e) {
  if (!isPlusProduct(e)) return false
  return isValidCouponCategory(getCouponProductCategory(e))
}

/**
 * 打印 Plus / category 产品类型日志
 * @param {Object} e ready 回调事件
 */
export function logCouponReadyProduct (e) {
  const scheme = getCouponPayScheme(e)
  const isPlus = isPlusProduct(e)
  const category = getCouponProductCategory(e)
  const entryEnabled = isCouponEntryEnabled(e)
  const extra = e && e.extra ? e.extra : null

  logger.info('[PyunAd] ready回调, 产品类型 scheme:', scheme || '(空)')
  logger.info('[PyunAd] ready回调, Plus:', isPlus, 'category:', category || '(空)', '券包入口:', entryEnabled)
  if (extra) {
    logger.info('[PyunAd] ready回调, extra.member_type:', extra.member_type || '(空)')
    logger.info('[PyunAd] ready回调, extra.cajole_text:', extra.cajole_text || '(空)')
  }
  console.log('[PyunAd] ready产品判断 Plus:', isPlus, 'category:', category, 'entryEnabled:', entryEnabled, {
    scheme,
    uri: e && e.uri,
    extra
  })
}

/**
 * 从 ready 回调解析券包支付主按钮文案（Plus 下按 category 区分）
 * @param {Object} e ready 回调事件
 * @returns {string}
 */
export function formatCouponPayTitle (e) {
  if (!isCouponEntryEnabled(e)) return ''
  const category = getCouponProductCategory(e)
  if (category === PYUN_COUPON_CATEGORY.BENEFER) return '安心支付'
  if (category === PYUN_COUPON_CATEGORY.PASSER) return '优惠支付'
  return ''
}

/**
 * 从 advertingPull ready 回调的 e.extra 解析「优惠支付」按钮副标题
 * @param {Object} extra PP 返回的业务附加参数
 * @returns {string}
 */
export function formatCouponPaySubText (extra) {
  if (!extra || typeof extra !== 'object') return ''

  if (extra.cajole_text != null && String(extra.cajole_text).trim()) {
    return String(extra.cajole_text).trim()
  }

  const textKeys = [
    'subtitle', 'sub_title', 'caption', 'desc', 'description',
    'btn_sub', 'btn_subtitle', 'tips', 'slogan', 'label'
  ]
  for (let i = 0; i < textKeys.length; i++) {
    const val = extra[textKeys[i]]
    if (val != null && String(val).trim()) {
      return String(val).trim()
    }
  }

  if (extra.price != null && !isNaN(Number(extra.price))) {
    return '支付金额￥' + (Number(extra.price) / 100).toFixed(2)
  }

  return ''
}

/**
 * 根据 ready 回调统一设置优惠支付按钮文案
 * @param {Object} vm 页面实例
 * @param {Object} e ready 回调事件
 */
export function applyCouponPayButtonFromReady (vm, e) {
  if (!vm) return
  const entryEnabled = isCouponEntryEnabled(e)
  vm.pyunCouponEntryEnabled = entryEnabled
  vm.pyunCouponCategory = getCouponProductCategory(e)
  if (entryEnabled) {
    vm.pyunCouponBtnTitle = formatCouponPayTitle(e)
    vm.pyunCouponBtnSub = formatCouponPaySubText(e && e.extra)
  } else {
    vm.pyunCouponBtnTitle = ''
    vm.pyunCouponBtnSub = ''
  }
}

/**
 * 拉取广告
 * @param {Object} options 配置项
 * @param {string} options.openid 用户openid
 * @param {string} options.plate 车牌号 (至少前两位)
 * @param {number} options.tradeValue 交易金额(分)
 * @param {string} options.el 广告容器节点ID（DOM元素ID）
 * @param {string} options.spaceId 广告位ID（可选，默认 pyunSpaceId）
 * @param {string} options.providerCode 流量主编号（可选）
 * @param {string} options.providerUrl 流量主同步回调地址（可选）
 * @param {Function} options.onReady advertingPull ready 回调，可通过 e.extra 获取业务参数
 * @returns {Promise<Object>} 广告数据
 */
export async function pullAdvert (options = {}) {
  try {
    const spaceId = options.spaceId || pyunSpaceId
    const el = options.el || 'pyun-ad-banner'

    logger.info('[PyunAd] ==================== 开始拉取广告 ====================')
    logger.info('[PyunAd] 配置参数:', {
      el: el,
      app_id: pyunAppId,
      space_id: spaceId,
      provider_code: options.providerCode || pyunProviderCode,
      external: pyunExternal,
      provider_url: options.providerUrl || pyunProviderUrl
    })
    logger.info('[PyunAd] 业务参数:', {
      openid: options.openid || '(空)',
      plate: options.plate || '(空)',
      tradeValue: options.tradeValue || 0
    })

    // 加载SDK
    logger.info('[PyunAd] 步骤1: 加载SDK...')
    const sdkLoaded = await loadPyunSdk()
    logger.info('[PyunAd] SDK加载结果:', sdkLoaded)
    logger.info('[PyunAd] SDK加载状态:', isPyunSdkLoaded())

    const pyun = getPyunInstance()
    logger.info('[PyunAd] pyun对象:', pyun ? '存在' : '不存在')

    if (!pyun || typeof pyun.advertingPull !== 'function') {
      logger.error('[PyunAd] SDK未正确加载或advertingPull方法不存在')
      throw new Error('SDK未加载')
    }

    const domReady = await waitForAdElement(el)
    if (!domReady) {
      const msg = `DOM节点 '${el}' 不存在`
      logger.error('[PyunAd] 广告加载失败', msg)
      throw new Error(msg)
    }

    // 返回 Promise 处理回调
    return new Promise((resolve, reject) => {
      const config = {
        el: el,
        app_id: pyunAppId,
        openid: options.openid || '',
        space_id: spaceId,
        displayed: 1,
        provider_code: options.providerCode || pyunProviderCode,
        external: pyunExternal,
        provider_url: options.providerUrl || pyunProviderUrl,
        personas: {
          plate: options.plate || '',
          trade_value: options.tradeValue || 0
        },
        success: (data) => {
          logger.info('[PyunAd] 广告加载成功!', data)
          const state = getAdState(spaceId)
          state.loaded = true
          state.data = data
          resolve(data)
        },
        failure: (cause) => {
          const errMsg = formatPyunError(cause)
          logger.error('[PyunAd] 广告加载失败', errMsg, cause)
          const state = getAdState(spaceId)
          state.loaded = false
          state.data = null
          state.extra = null
          state.uri = null
          reject(new Error(errMsg))
        }
      }

      if (typeof options.onReady === 'function') {
        config.ready = (e) => {
          const extra = e && e.extra ? e.extra : null
          const state = getAdState(spaceId)
          state.extra = extra
          state.uri = e && e.uri ? e.uri : null
          logCouponReadyProduct(e)
          logger.info('[PyunAd] ready回调, extra:', extra)
          options.onReady(e)
        }
      } else {
        config.ready = (e) => {
          const extra = e && e.extra ? e.extra : null
          const state = getAdState(spaceId)
          state.extra = extra
          state.uri = e && e.uri ? e.uri : null
          logCouponReadyProduct(e)
          logger.info('[PyunAd] ready回调, extra:', extra)
        }
      }

      logger.info('[PyunAd] 步骤2: 调用 advertingPull, spaceId:', spaceId)
      pyun.advertingPull(config)
    })
  } catch (err) {
    logger.error('[PyunAd] 拉取广告异常', err)
    throw err
  }
}

/**
 * 广告拦截 (支付时调用)
 * @param {Function} onComplete 拦截回调
 * @param {boolean} enforce 是否强制执行拦截（无弹窗模式传 true）
 * @param {string} spaceId 广告位ID（可选，默认 pyunSpaceId）
 */
export function interceptAdvert (onComplete, enforce = true, spaceId) {
  const sid = spaceId || pyunSpaceId
  const state = getAdState(sid)
  logger.info('[PyunAd] ==================== 执行广告拦截 ====================')
  logger.info('[PyunAd] 当前广告状态:', { loaded: state.loaded, data: state.data })

  const pyun = getPyunInstance()
  if (!pyun || typeof pyun.advertingIntercept !== 'function') {
    logger.error('[PyunAd] SDK未正确加载或advertingIntercept方法不存在')
    if (typeof onComplete === 'function') {
      onComplete({ action: 'NONE' })
    }
    return
  }

  logger.info('[PyunAd] 调用 advertingIntercept, enforce:', enforce)
  pyun.advertingIntercept((event) => {
    logger.info('[PyunAd] 拦截回调触发:', event)
    if (typeof onComplete === 'function') {
      onComplete(event)
    }
  }, enforce)
}

/**
 * 广告是否已加载
 * @param {string} spaceId 广告位ID（可选，默认 pyunSpaceId）
 * @returns {boolean}
 */
export function isAdLoaded (spaceId) {
  return getAdState(spaceId || pyunSpaceId).loaded
}

/**
 * 获取广告数据
 * @param {string} spaceId 广告位ID（可选，默认 pyunSpaceId）
 * @returns {Object|null}
 */
export function getAdData (spaceId) {
  return getAdState(spaceId || pyunSpaceId).data
}

/**
 * 获取券包 ready 回调中的业务附加参数
 * @param {string} spaceId 广告位ID（可选，默认 pyunSpaceId）
 * @returns {Object|null}
 */
export function getAdExtra (spaceId) {
  return getAdState(spaceId || pyunSpaceId).extra
}

/**
 * 重置广告状态
 * @param {string} spaceId 广告位ID（可选，不传则重置所有）
 */
export function resetAdStatus (spaceId) {
  if (spaceId) {
    const state = getAdState(spaceId)
    state.loaded = false
    state.data = null
    state.extra = null
    state.uri = null
  } else {
    // 重置所有
    Object.keys(adStateMap).forEach(key => {
      adStateMap[key].loaded = false
      adStateMap[key].data = null
      adStateMap[key].extra = null
      adStateMap[key].uri = null
    })
  }
}
